/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The shapes the geofence screens work in. Mirrors api-insights' `ManagedGeofence` /
 * `ManagedGeofenceDetail` schemas and the geofence write placeholders.
 */

export type ShapeType = 'polygon' | 'rectangle' | 'circle'

/** Fresh's `ComplexityTypeId`: how the shape was drawn. All three are stored as points. */
export const COMPLEXITY_TYPE_IDS: Record<ShapeType, 1 | 2 | 3> = { circle: 1, polygon: 2, rectangle: 3 }

export const shapeTypeOf = (complexityTypeId: number | null): ShapeType =>
  complexityTypeId === 1 ? 'circle' : complexityTypeId === 3 ? 'rectangle' : 'polygon'

export const SHAPE_LABELS: Record<ShapeType, string> = { polygon: 'Polygon', rectangle: 'Rectangle', circle: 'Circle' }

/** Category type a user can set: low fuel. Any other category has none. */
export const LOW_FUEL_CATEGORY_TYPE_ID = 4

export interface GeofenceBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface ManagedGeofence {
  id: number
  name: string
  companyId: number
  version: number
  zorder: number
  visible: boolean
  complexityTypeId: number | null
  categoryIds: number[]
  bounds: GeofenceBounds | null
  /** The outline as [latitude, longitude] pairs, in point order. */
  coordinates: [number, number][]
}

export interface GeofencesByBoundariesResult {
  ids: number[]
  geofences: ManagedGeofence[]
}

export interface GeofencePoint {
  latitude: number
  longitude: number
  pointIndex: number
}

export interface GeofenceDetail extends Omit<ManagedGeofence, 'coordinates'> {
  points: GeofencePoint[]
}

export interface GeofenceCategory {
  id: number
  name: string
  companyId: number
  /** 4 for a low fuel category, otherwise null. */
  typeId: number | null
}

/**
 * A drawn shape: a closed ring of [longitude, latitude] positions (GeoJSON order), first and
 * last equal.
 */
export interface GeofenceShape {
  type: ShapeType
  ring: [number, number][]
}

export interface GeofenceFormValues {
  name: string
  categoryIds: number[]
  zorder: number
  visible: boolean
}

export interface GeofenceWriteBody extends GeofenceFormValues {
  complexityTypeId: 1 | 2 | 3
  points: GeofencePoint[]
}

export interface GeofenceCategoryWriteBody {
  name: string
  geofenceCategoryTypeId: number | null
}

/** Geofence list filters, kept in the query string so a link reproduces the list. */
export interface GeofenceFilters {
  search: string
  /** Category ids as strings, plus `NOT_CATEGORISED`. Empty means every geofence. */
  categories: string[]
  visibility: 'all' | 'visible' | 'hidden'
  /** Z-orders 1–5. Empty means every layer. */
  layers: number[]
}

export const NOT_CATEGORISED = 'none'

export type SortKey = 'name' | 'zorder' | 'visible'
export interface SortState {
  key: SortKey
  ascending: boolean
}

export interface GeofenceEditorNavigationState {
  geofence?: GeofenceDetail
}
